import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, RefreshCw, FileText, Download, CheckCircle, XCircle, Edit } from 'lucide-react';
import api from 'services/api';
import { usePermissions } from 'hooks/usePermissions';

const ModalDetalhesConciliacao = ({ isOpen, onClose, conciliacaoId }) => {
  const { hasPermission } = usePermissions();
  const podeEditarConciliacao = hasPermission('administrativo.edit');
  const [detalhes, setDetalhes] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [abaAtiva, setAbaAtiva] = useState('conciliadas');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [termoPesquisa, setTermoPesquisa] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [tamanhoPagina] = useState(50);
  const [baixandoOfx, setBaixandoOfx] = useState(false);
  const closeButtonRef = useRef(null);

  const carregarDetalhes = useCallback(async () => {
    if (!conciliacaoId) return;

    setCarregando(true);
    setErro(null);

    try {
      const response = await api.get(`/n8n/administracao/conciliacao-bancaria/${conciliacaoId}/detalhes`);

      if (response.data && response.data.success) {
        setDetalhes(response.data.data);
      } else {
        setErro(response.data?.message || 'Erro ao carregar detalhes');
      }
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
      setErro(err.response?.data?.message || 'Erro ao carregar detalhes da conciliação');
    } finally {
      setCarregando(false);
    }
  }, [conciliacaoId]);

  useEffect(() => {
    if (isOpen && conciliacaoId) {
      carregarDetalhes();
    } else {
      // Resetar estado ao fechar
      setDetalhes(null);
      setErro(null);
      setAbaAtiva('conciliadas');
      setFiltroTipo('todos');
      setTermoPesquisa('');
      setPaginaAtual(1);
    }
  }, [isOpen, conciliacaoId, carregarDetalhes]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  const handleDownloadOfx = async () => {
    if (!conciliacaoId) return;

    setBaixandoOfx(true);
    try {
      const response = await api.get(
        `/n8n/administracao/conciliacao-bancaria/${conciliacaoId}/download-ofx`,
        { 
          responseType: 'blob',
          // Garantir que os headers sejam expostos
          headers: {
            'Accept': 'application/x-ofx'
          }
        }
      );

      // Criar link temporário para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Obter nome do arquivo do header ou usar padrão
      // Axios pode não expor todos os headers por padrão, verificar todas as possibilidades
      const contentDisposition = 
        response.headers['content-disposition'] || 
        response.headers['Content-Disposition'] ||
        response.headers.get?.('content-disposition');
      
      let filename = `conciliacao_${conciliacaoId}.ofx`;
      
      if (contentDisposition) {
        // Tentar diferentes padrões de extração do nome
        // Padrão 1: filename="nome_arquivo.ofx"
        let filenameMatch = contentDisposition.match(/filename="([^"]+)"/i);
        if (!filenameMatch) {
          // Padrão 2: filename=nome_arquivo.ofx (sem aspas)
          filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
        }
        if (!filenameMatch) {
          // Padrão 3: filename*=UTF-8''nome_arquivo.ofx (RFC 5987)
          filenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        }
        
        if (filenameMatch && filenameMatch[1]) {
          // Decodificar se necessário (para caracteres especiais)
          try {
            filename = decodeURIComponent(filenameMatch[1].trim());
          } catch {
            filename = filenameMatch[1].trim();
          }
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao baixar OFX:', err);
      alert('Erro ao baixar arquivo OFX. Tente novamente.');
    } finally {
      setBaixandoOfx(false);
    }
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data) => {
    if (!data) return '-';
    try {
      // Remover horário se existir (formato DD/MM/YYYY HH:MM:SS)
      const matchBrComHora = data.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+\d{2}:\d{2}:\d{2}$/);
      if (matchBrComHora) {
        return `${matchBrComHora[1]}/${matchBrComHora[2]}/${matchBrComHora[3]}`;
      }

      // Formato DD/MM/YYYY sem horário
      const matchBr = data.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (matchBr) {
        return data;
      }

      // Formato ISO YYYY-MM-DD
      const matchIso = data.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (matchIso) {
        return `${matchIso[3]}/${matchIso[2]}/${matchIso[1]}`;
      }

      // Tentar parsear como Date e formatar apenas a data
      const date = new Date(data);
      if (Number.isNaN(date.getTime())) {
        return data;
      }

      return date.toLocaleDateString('pt-BR');
    } catch {
      return data;
    }
  };

  const renderTipoBadge = (tipo) => {
    if (!tipo) return null;
    const normalizado = tipo.toUpperCase();
    const isDebit = normalizado === 'DEBIT';
    const classes = isDebit
      ? 'border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]'
      : 'border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--accent)]';
    
    const label = isDebit ? 'DÉBITO' : 'CRÉDITO';
    
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded border ${classes}`}>
        {label}
      </span>
    );
  };

  if (!isOpen) return null;

  const linhas = detalhes?.linhas || [];
  const estatisticas = detalhes?.estatisticas || {};

  // Filtrar linhas por categoria
  const linhasPorCategoria = {
    conciliadas: linhas.filter(l => l.categoria === 'conciliadas'),
    pendencias_api: linhas.filter(l => l.categoria === 'pendencias_api'),
    pendencias_ofx: linhas.filter(l => l.categoria === 'pendencias_ofx'),
    duplicidades: linhas.filter(l => l.categoria === 'duplicidades')
  };

  const abas = [
    { id: 'conciliadas', label: 'Conciliadas', count: linhasPorCategoria.conciliadas.length },
    { id: 'pendencias_api', label: 'Pendências API', count: linhasPorCategoria.pendencias_api.length },
    { id: 'pendencias_ofx', label: 'Pendências OFX', count: linhasPorCategoria.pendencias_ofx.length },
    { id: 'duplicidades', label: 'Duplicidades', count: linhasPorCategoria.duplicidades.length }
  ];

  // Aplicar filtros
  const dadosFiltrados = linhasPorCategoria[abaAtiva]
    .filter((item) => {
      if (filtroTipo === 'todos') return true;
      const tipo = (item.tipo || '').toUpperCase();
      if (filtroTipo === 'credito') return tipo === 'CREDIT';
      if (filtroTipo === 'debito') return tipo === 'DEBIT';
      return true;
    })
    .filter((item) => {
      if (!termoPesquisa.trim()) return true;
      const termo = termoPesquisa.toLowerCase().trim();
      const fornecedor = (item.fornecedor || '').toLowerCase();
      const valor = String(item.valor || '');
      return fornecedor.includes(termo) || valor.includes(termo);
    })
    .sort((a, b) => {
      const dataA = a.data ? new Date(a.data) : new Date(0);
      const dataB = b.data ? new Date(b.data) : new Date(0);
      return dataA - dataB;
    });

  const totalRegistros = dadosFiltrados.length;
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / tamanhoPagina));
  const indiceInicio = (paginaAtual - 1) * tamanhoPagina;
  const indiceFim = indiceInicio + tamanhoPagina;
  const dadosPagina = dadosFiltrados.slice(indiceInicio, indiceFim);

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-3 py-4 text-center sm:px-4 sm:py-6">
        <div
          className="fixed inset-0 bg-black/60 transition-opacity"
          onClick={onClose}
        />

        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="detalhes-conciliacao-title"
          className="relative my-2 inline-block w-full max-w-[96rem] text-left align-middle"
        >
          <div className="max-h-[92dvh] overflow-y-auto rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-2xl transition-all sm:p-6">
          {/* Header */}
          <div className="flex flex-col gap-3 border-b border-[color:var(--border)] pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h3 id="detalhes-conciliacao-title" className="text-lg font-semibold text-[color:var(--text-primary)]">
                Detalhes da Conciliação
              </h3>
              <p className="text-sm text-[color:var(--text-muted)] mt-1">
                {detalhes ? `ID: ${detalhes.id} • Versão ${detalhes.versao}` : 'Carregando...'}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {detalhes && (
                <>
                  {podeEditarConciliacao && (
                    <button
                      onClick={() => {
                        localStorage.setItem('conciliacao_editar_id', detalhes.id.toString());
                        window.dispatchEvent(new CustomEvent('navigate-to-conciliacao'));
                        onClose();
                      }}
                      className="inline-flex h-11 min-w-[2.75rem] items-center justify-center rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                      title="Editar conciliação"
                      aria-label="Editar conciliação"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={handleDownloadOfx}
                    disabled={baixandoOfx}
                    className="inline-flex h-11 items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-4 text-sm font-medium text-[color:var(--accent)] transition-colors hover:bg-[color:var(--surface)] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                  >
                    <Download className="h-4 w-4" />
                    {baixandoOfx ? 'Baixando...' : 'Download OFX'}
                  </button>
                </>
              )}
              <button
                ref={closeButtonRef}
                onClick={onClose}
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface)] hover:text-[color:var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                aria-label="Fechar detalhes da conciliação"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Conteúdo */}
          {carregando ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 text-[color:var(--accent)] animate-spin mx-auto mb-4" />
              <p className="text-[color:var(--text-muted)]">Carregando detalhes...</p>
            </div>
          ) : erro ? (
            <div className="p-8 text-center">
              <FileText className="h-12 w-12 text-[color:var(--text-muted)] mx-auto mb-4" />
              <p className="text-[color:var(--text-muted)]">{erro}</p>
              <button
                onClick={carregarDetalhes}
                className="mt-4 inline-flex h-10 items-center justify-center rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-4 text-sm font-medium text-[color:var(--accent)] transition-colors hover:bg-[color:var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
              >
                Tentar novamente
              </button>
            </div>
          ) : detalhes ? (
            <div className="mt-6 space-y-6">
              {/* Informações Gerais */}
              <div className="grid grid-cols-1 gap-4 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-4 sm:grid-cols-2 xl:grid-cols-3">
                <div>
                  <p className="text-xs font-medium text-[color:var(--text-muted)]">Data da Conciliação</p>
                  <p className="text-sm text-[color:var(--text-primary)] mt-1">
                    {formatarData(detalhes.data_conciliacao)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-[color:var(--text-muted)]">Versão</p>
                  <p className="text-sm text-[color:var(--text-primary)] mt-1">
                    {detalhes.versao}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-[color:var(--text-muted)]">Banco</p>
                  <p className="text-sm text-[color:var(--text-primary)] mt-1">
                    {detalhes.banco || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-[color:var(--text-muted)]">Conta</p>
                  <p className="text-sm text-[color:var(--text-primary)] mt-1">
                    {detalhes.conta || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-[color:var(--text-muted)]">Período</p>
                  <p className="text-sm text-[color:var(--text-primary)] mt-1">
                    {formatarData(detalhes.periodo_inicio)} até {formatarData(detalhes.periodo_fim)}
                  </p>
                </div>
                {detalhes.criado_por && (
                  <div>
                    <p className="text-xs font-medium text-[color:var(--text-muted)]">Criado por</p>
                    <p className="text-sm text-[color:var(--text-primary)] mt-1">
                      {detalhes.criado_por.full_name || detalhes.criado_por.corporate_email}
                    </p>
                  </div>
                )}
              </div>

              {/* KPIs - Estatísticas */}
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-4">
                  <div className="mb-1 text-xs font-medium text-[color:var(--text-muted)]">Conciliadas</div>
                  <div className="text-2xl font-bold text-[color:var(--text-primary)]">
                    {estatisticas.conciliadas || 0}
                  </div>
                </div>
                <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-4">
                  <div className="mb-1 text-xs font-medium text-[color:var(--text-muted)]">Pendências API</div>
                  <div className="text-2xl font-bold text-[color:var(--text-primary)]">
                    {estatisticas.pendencias_api || 0}
                  </div>
                </div>
                <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-4">
                  <div className="mb-1 text-xs font-medium text-[color:var(--text-muted)]">Pendências OFX</div>
                  <div className="text-2xl font-bold text-[color:var(--text-primary)]">
                    {estatisticas.pendencias_ofx || 0}
                  </div>
                </div>
                <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-4">
                  <div className="mb-1 text-xs font-medium text-[color:var(--text-muted)]">Duplicidades</div>
                  <div className="text-2xl font-bold text-[color:var(--text-primary)]">
                    {estatisticas.duplicidades || 0}
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-[color:var(--border)]">
                <nav className="-mb-px flex flex-wrap gap-3">
                  {abas.map((aba) => (
                    <button
                      key={aba.id}
                      onClick={() => {
                        setAbaAtiva(aba.id);
                        setPaginaAtual(1);
                      }}
                      className={`
                        rounded-t-lg border-b-2 px-1 py-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35
                        ${
                          abaAtiva === aba.id
                            ? 'border-[color:var(--accent)] text-[color:var(--accent)]'
                            : 'border-transparent text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:border-[color:var(--border)]'
                        }
                      `}
                    >
                      {aba.label} ({aba.count})
                    </button>
                  ))}
                </nav>
              </div>

              {/* Filtros */}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto] md:items-end">
                <div>
                  <label htmlFor="pesquisa-detalhes-conciliacao" className="mb-1 block text-xs font-medium text-[color:var(--text-muted)]">
                    Pesquisar
                  </label>
                  <input
                    id="pesquisa-detalhes-conciliacao"
                    type="text"
                    placeholder="Pesquisar por fornecedor ou valor..."
                    value={termoPesquisa}
                    onChange={(e) => {
                      setTermoPesquisa(e.target.value);
                      setPaginaAtual(1);
                    }}
                    className="h-11 w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-3 text-sm text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                  />
                </div>
                <div>
                  <label htmlFor="filtro-tipo-detalhes-conciliacao" className="mb-1 block text-xs font-medium text-[color:var(--text-muted)]">
                    Tipo
                  </label>
                <select
                  id="filtro-tipo-detalhes-conciliacao"
                  value={filtroTipo}
                  onChange={(e) => {
                    setFiltroTipo(e.target.value);
                    setPaginaAtual(1);
                  }}
                  className="h-11 min-w-[12rem] rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-3 text-sm text-[color:var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                >
                  <option value="todos">Todos os tipos</option>
                  <option value="credito">Crédito</option>
                  <option value="debito">Débito</option>
                </select>
                </div>
              </div>

              {/* Tabela */}
              <div className="border border-[color:var(--border)] rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[color:var(--border)]">
                    <thead className="bg-[color:var(--surface-muted)]">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider border-r border-[color:var(--border)]">
                          Valor / Data / Fornecedor
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider border-r border-[color:var(--border)]">
                          Tem no IXC
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                          Tem no OFX
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--border)]">
                      {dadosPagina.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-6 py-8 text-center text-sm text-[color:var(--text-muted)]">
                            Nenhum registro encontrado para o filtro selecionado.
                          </td>
                        </tr>
                      ) : (
                        dadosPagina.map((item, index) => (
                          <tr key={index} className="hover:bg-[color:var(--surface-muted)] transition-colors">
                            <td className="border-r border-[color:var(--border)] px-6 py-4">
                              <div className="space-y-1">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                                    {formatarMoeda(item.valor)}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {renderTipoBadge(item.tipo)}
                                  </div>
                                </div>
                                <div className="text-xs text-[color:var(--text-muted)]">
                                  {formatarData(item.data)}
                                </div>
                                <div className="text-xs text-[color:var(--text-muted)]">
                                  {item.fornecedor || '-'}
                                </div>
                              </div>
                            </td>
                            <td className="border-r border-[color:var(--border)] px-6 py-4 text-center">
                              {item.tem_no_ixc ? (
                                <>
                                  <CheckCircle className="mx-auto h-5 w-5 text-[color:var(--accent)]" aria-hidden="true" />
                                  <span className="sr-only">Disponível no IXC</span>
                                </>
                              ) : (
                                <>
                                  <XCircle className="mx-auto h-5 w-5 text-[color:var(--text-muted)]" aria-hidden="true" />
                                  <span className="sr-only">Não disponível no IXC</span>
                                </>
                              )}
                            </td>
                            <td className="px-6 py-4 text-center">
                              {item.tem_no_ofx ? (
                                <>
                                  <CheckCircle className="mx-auto h-5 w-5 text-[color:var(--accent)]" aria-hidden="true" />
                                  <span className="sr-only">Disponível no OFX</span>
                                </>
                              ) : (
                                <>
                                  <XCircle className="mx-auto h-5 w-5 text-[color:var(--text-muted)]" aria-hidden="true" />
                                  <span className="sr-only">Não disponível no OFX</span>
                                </>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Paginação */}
              {totalPaginas > 1 && (
                <div className="flex flex-col gap-3 border-t border-[color:var(--border)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-[color:var(--text-muted)]">
                    Mostrando {indiceInicio + 1} até {Math.min(indiceFim, totalRegistros)} de {totalRegistros} resultados
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPaginaAtual(p => Math.max(1, p - 1))}
                      disabled={paginaAtual === 1}
                      className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-1 text-sm text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                    >
                      Anterior
                    </button>
                    <span className="px-3 py-1 text-sm text-[color:var(--text-primary)]">
                      Página {paginaAtual} de {totalPaginas}
                    </span>
                    <button
                      onClick={() => setPaginaAtual(p => Math.min(totalPaginas, p + 1))}
                      disabled={paginaAtual === totalPaginas}
                      className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-1 text-sm text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
        </div>
      </div>
    </div>
  );
};

export default ModalDetalhesConciliacao;
